/**
 * WO-D2.M — per-account comms config (comms.manage): senders, BYO secrets, inbound address.
 *
 * WO-M0 — rebuilt on the design system: Screen / Card / Field / Button / Text.
 */
import { useState } from 'react';
import { ActivityIndicator, Alert, StyleSheet, View } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listIdentities,
  upsertIdentity,
  setIdentitySecret,
  getInboundAddress,
  rotateInboundAddress,
  type CommsIdentity,
} from '../../../../src/api/account';
import { ApiRequestError } from '../../../../src/api/client';
import { Screen, Text, Card, Field, Button } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';

export default function CommsScreen() {
  const qc = useQueryClient();
  const identities = useQuery({ queryKey: ['comms-identities'], queryFn: listIdentities });
  const inbound = useQuery({ queryKey: ['inbound-address'], queryFn: getInboundAddress });

  async function handleRotate() {
    try {
      await rotateInboundAddress();
      qc.invalidateQueries({ queryKey: ['inbound-address'] });
    } catch (e) {
      Alert.alert('Could not rotate', e instanceof ApiRequestError ? e.message : 'Error');
    }
  }

  return (
    <Screen scroll>
      <Text variant="title">Text & email senders</Text>
      <Text variant="body" color="secondary">
        Configure how messages go out from your account, and where inbound items arrive. Secrets are
        write-only — we only ever show the last 4 characters.
      </Text>

      {identities.isLoading ? (
        <ActivityIndicator accessibilityLabel="Loading identities" />
      ) : null}
      {identities.data?.map((m) => (
        <IdentityCard
          key={m.id}
          m={m}
          onChanged={() => qc.invalidateQueries({ queryKey: ['comms-identities'] })}
        />
      ))}

      <Card>
        <Text variant="label" color="secondary">Inbound intake address</Text>
        {inbound.isLoading ? (
          <ActivityIndicator accessibilityLabel="Loading inbound address" />
        ) : (
          <Text variant="data" selectable accessibilityLabel="Inbound email address">
            {inbound.data?.address ?? '…'}
          </Text>
        )}
        <Button
          title="Rotate address"
          variant="secondary"
          size="md"
          onPress={handleRotate}
          accessibilityLabel="Rotate inbound address"
        />
      </Card>
    </Screen>
  );
}

function IdentityCard({ m, onChanged }: { m: CommsIdentity; onChanged: () => void }) {
  const [from, setFrom] = useState(m.from);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const secretLabel = m.secrets[0]?.last4 ? `secret •••• ${m.secrets[0].last4}` : 'no secret set';

  async function saveFrom() {
    setBusy(true);
    try {
      await upsertIdentity({
        id: m.id,
        channel: m.channel as 'sms' | 'email',
        provider: m.provider as 'twilio' | 'sendgrid',
        from_number_or_address: from,
      });
      onChanged();
    } catch (e) {
      Alert.alert('Could not save', e instanceof ApiRequestError ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  async function saveSecret() {
    if (!secret) return;
    setBusy(true);
    try {
      await setIdentitySecret(m.id, secret);
      setSecret('');
      onChanged();
      Alert.alert('Saved', 'Credential stored securely.');
    } catch (e) {
      Alert.alert('Could not save secret', e instanceof ApiRequestError ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Text variant="label" color="secondary">
        {m.channel.toUpperCase()} · {m.provider}
      </Text>
      <Field
        label={m.channel === 'sms' ? 'From number' : 'From address'}
        value={from}
        onChangeText={setFrom}
        autoCapitalize="none"
        editable={!busy}
        accessibilityLabel={m.channel === 'sms' ? 'From phone number' : 'From email address'}
      />
      <Button
        title="Save sender"
        variant="primary"
        size="md"
        onPress={saveFrom}
        loading={busy}
        disabled={busy}
        accessibilityLabel="Save sender"
      />

      <View style={styles.secretSection}>
        <Text variant="label" color="secondary">API credential</Text>
        <Text variant="data" color="secondary">{secretLabel}</Text>
      </View>
      <Field
        label="New secret"
        value={secret}
        onChangeText={setSecret}
        placeholder="paste new secret"
        secureTextEntry
        autoCapitalize="none"
        editable={!busy}
        accessibilityLabel="New API credential"
      />
      <Button
        title="Update credential"
        variant="secondary"
        size="md"
        onPress={saveSecret}
        disabled={!secret || busy}
        loading={busy && !!secret}
        accessibilityLabel="Update API credential"
      />
    </Card>
  );
}

const styles = StyleSheet.create({
  secretSection: { gap: theme.spacing.xs, marginTop: theme.spacing.sm },
});
